import { NewPasswordInput } from "@/components/auth/new-password-input";
import { Button } from "@/components/ui/button";
import { checkPasswordStrength, debounce } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { createFileRoute, useRouter, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { validateResetPasswordForm } from "@/lib/utils/register";
import { useStytch } from "@stytch/react";
import { toast } from "@/components/ui/use-toast";
import { getPasswordResetErrorMessage } from "@/lib/utils/stytch-errors";

export const Route = createFileRoute("/_auth/reset-password")({
  component: ResetPassword,
});

function ResetPassword() {
  const stytchClient = useStytch();
  const router = useRouter();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState<ReturnType<
    typeof checkPasswordStrength
  > | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);

  const search = useSearch({ from: "/_auth/reset-password" }) as {
    token?: string;
  };
  const token = search.token;

  const handleReset = useCallback(async () => {
    setLoading(true);
    try {
      const response = await stytchClient.passwords.resetByEmail({
        password,
        token: token || "",
        session_duration_minutes: 60, // 1 hour
      });

      if (response.session.user_id) {
        // success, redirect to login

        toast({
          title: "Password reset successful",
          description:
            "You can now login with your new password. Redirecting in 3 seconds...",
        });

        setTimeout(() => {
          router.navigate({ to: "/login" });
        }, 3000);
      }
    } catch (e) {
      console.error(e);
      setError(getPasswordResetErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [stytchClient, router, toast, token, password]);

  const validateForm = useCallback(
    debounce(() => {
      const validation = validateResetPasswordForm(
        password,
        confirmPassword,
        passwordStrength,
      );
      setIsFormValid(validation.isValid);
      if (!validation.isValid) {
        setError(validation.error || null);
      } else {
        setError(null);
      }
    }, 300),
    [password, confirmPassword, passwordStrength],
  );

  useEffect(() => {
    validateForm();
  }, [validateForm]);

  const handlePasswordChange = useCallback(
    (newPassword: string) => {
      setPassword(newPassword);
      setPasswordStrength(checkPasswordStrength(newPassword));
    },
    [setPassword, setPasswordStrength],
  );

  return (
    <div className="flex h-screen w-full flex-row">
      <div className="flex w-[50%] items-center justify-center bg-[#0038FF10]">
        <img src="assets/frame.png" alt="Frame" />
      </div>
      <div className="flex w-[50%] flex-col items-center justify-center">
        <div className="w-[600px]">
          <h1 className="text-5xl font-bold leading-tight">
            Somethings <br />
            <span className="text-gray-300">Reset Password</span>
          </h1>

          <div className="mt-16">
            <NewPasswordInput
              value={password}
              onChange={handlePasswordChange}
              placeholder="Enter your password"
              showConfirmation={true}
              confirmValue={confirmPassword}
              onConfirmChange={setConfirmPassword}
              confirmPlaceholder="Confirm your password"
            />
            {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
          </div>
          <Button
            onClick={handleReset}
            className="mt-4 h-12 w-full rounded-lg text-lg font-bold"
            disabled={loading || !isFormValid}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Reset Password
          </Button>
          <p className="mt-4 text-sm text-gray-500">
            Having trouble logging in or resetting your password? Contact your
            supervisor
          </p>
        </div>
      </div>
    </div>
  );
}
